/**
 * Pure-function policy for how to display an incoming call in the browser.
 *
 * Browser equivalent of iOS `CallKitDisplayPolicy.swift`. The web platform
 * has no CXProvider, but the same threat model applies: peer-claimed strings
 * that arrive over the network can be server-influenced and MUST NEVER reach
 * the display layer. The display name is derived exclusively from the local
 * roster.
 *
 * Three independent data-leak vectors are controlled here:
 *
 *   1. Display name. The peer-claimed name in the signalling payload is
 *      deliberately excluded from the function signature — it cannot reach
 *      this function. Only the locally-trusted roster name is shown;
 *      otherwise we render an opaque userId prefix or the fixed literal
 *      "Unknown caller".
 *
 *   2. Handle value. On iOS, CXHandle type `.generic` prevents the OS from
 *      indexing the userId against Contacts/Spotlight. On the web, the
 *      Notification API title is the closest analogue; we provide a typed
 *      `handleValue` field that consumers MUST pass as-is and MUST NOT use
 *      as a lookup key into any system contact store.
 *
 *   3. History and DND policies are in separate modules
 *      (`CallHistoryPolicy.ts`, `CallDNDPolicy.ts`) so this module stays
 *      focused on the display decision.
 *
 * All decisions are pure functions of (CallerProfile, PeerCallSettings,
 * callUiEnabled). The caller assembles CallerProfile from its own roster —
 * never from the signalling payload.
 *
 * Wiring to the Notification API, in-app call UI, and local history store is
 * a consumer concern; this module has no imports from any browser API.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Local view of the caller assembled from our own roster — never from the
 * signalling payload. `trustedDisplayName` is undefined if we have no
 * human-readable name pinned for this userId.
 *
 * Web mirror of iOS `CallerProfile`.
 *
 * INVARIANT: there is deliberately no `claimedDisplayName` field. Peer /
 * server-provided name strings can never appear in this type.
 */
export interface CallerProfile {
  readonly userId: string;
  readonly trustedDisplayName?: string;
  readonly isInRoster: boolean;
}

/**
 * Per-peer call settings. The user configures these in the peer's settings
 * panel; conservative defaults for a fresh peer are
 * `{ suppressFromHistory: false, showCallerName: true, allowDNDOverride: false }`.
 *
 * Web mirror of iOS `PeerCallSettings`.
 */
export interface PeerCallSettings {
  /**
   * When true, the call MUST NOT appear in local call history after it ends.
   * Implemented by the post-call `CallHistoryPolicy`; there is no in-call
   * API to suppress the entry at ring time.
   */
  readonly suppressFromHistory: boolean;
  /**
   * When false, the display layer shows the userId prefix instead of the
   * trusted name, even if a trusted name exists.
   */
  readonly showCallerName: boolean;
  /**
   * When true, this peer's calls may override the browser's DND state.
   * Opt-in only; default is false.
   */
  readonly allowDNDOverride: boolean;
}

/**
 * What the consumer passes to `Notification`, the in-app call banner, or any
 * other display surface when an incoming call arrives.
 *
 * Web mirror of iOS `CallKitDisplayDecision`.
 *
 * `handleValue` is always the raw userId — consumers MUST NOT interpret it as
 * a phone number or email address; it must be treated as an opaque identifier.
 */
export interface CallDisplayDecision {
  /** Always the raw userId. Never a phone number or email. */
  readonly handleValue: string;
  /**
   * The string to show in the call banner / Notification title.
   * Derived from the local roster only; the peer-claimed name cannot appear
   * here.
   */
  readonly displayName: string;
  /** Whether the in-app UI should offer a "hold" button (mirrors iOS). */
  readonly supportsHolding: boolean;
  /**
   * Whether the in-app UI should offer DTMF input. False — we have no PSTN
   * bridge (mirrors iOS `supportsDTMF: false`).
   */
  readonly supportsDTMF: false;
  /**
   * Whether this call should be persisted to local call history. Mirrors iOS
   * `reportToSystemRecents = !suppressFromRecents`.
   */
  readonly recordInHistory: boolean;
  /**
   * Whether this call may override the browser DND state. Mirrors iOS
   * `bypassDND = allowDNDOverride`. Actual DND enforcement is the caller's
   * responsibility via `CallDNDPolicy`.
   */
  readonly bypassDND: boolean;
}

// ---------------------------------------------------------------------------
// Policy
// ---------------------------------------------------------------------------

/**
 * Decide how to display an incoming call.
 *
 * Returns `undefined` when `callUiEnabled` is false — the caller MUST then
 * show a silent in-page indicator instead of ringing or creating a
 * Notification. We never half-report a call.
 *
 * Web mirror of iOS `CallKitDisplayPolicy.decide`.
 */
export function decideCallDisplay(
  caller: CallerProfile,
  settings: PeerCallSettings,
  callUiEnabled: boolean,
): CallDisplayDecision | undefined {
  if (!callUiEnabled) return undefined;

  return {
    handleValue: caller.userId,
    displayName: resolveDisplayName(caller, settings),
    supportsHolding: true,
    supportsDTMF: false,
    recordInHistory: !settings.suppressFromHistory,
    bypassDND: settings.allowDNDOverride,
  };
}

/**
 * Resolve the display name from our own roster state.
 *
 * NOTE: there is deliberately no `claimedDisplayName` parameter — peer /
 * server-provided name strings can never reach this function. The display
 * name is a pure function of our own roster.
 *
 * | isInRoster | trustedDisplayName | showCallerName | Result                    |
 * |------------|--------------------|----------------|---------------------------|
 * | false      | any                | any            | "Unknown caller"          |
 * | true       | non-empty          | true           | the trusted name          |
 * | true       | non-empty          | false          | 8-char userId prefix + …  |
 * | true       | empty / undefined  | any            | 8-char userId prefix + …  |
 */
function resolveDisplayName(
  caller: CallerProfile,
  settings: PeerCallSettings,
): string {
  if (!caller.isInRoster) {
    return "Unknown caller";
  }
  if (!settings.showCallerName) {
    return userIdPrefix(caller.userId);
  }
  if (caller.trustedDisplayName !== undefined && caller.trustedDisplayName !== "") {
    return caller.trustedDisplayName;
  }
  return userIdPrefix(caller.userId);
}

/**
 * 8-character prefix + ellipsis. Used when the peer is in the roster but we
 * have no human-readable name, or when the user has chosen to hide names.
 *
 * Mirrors iOS `CallKitDisplayPolicy.userIdPrefix`.
 */
function userIdPrefix(userId: string): string {
  if (userId.length <= 8) return userId + "…";
  return userId.slice(0, 8) + "…";
}
